import { API } from "./api";
import { useEffect, useState } from "react";

/**
 * SEC-003 · Ask the backend for a 10-minute scope-limited media token, then
 * return the streaming URL with that token in `?auth=`. The full-scope JWT
 * never travels in a URL.
 */
export async function mediaSrc(resourceType, resourceId) {
  const url = `${API}/media-tokens?resource_type=${encodeURIComponent(resourceType)}&resource_id=${encodeURIComponent(resourceId)}`;
  const bearer = localStorage.getItem("fl_token");
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: bearer ? { Authorization: `Bearer ${bearer}` } : {},
  });
  if (!res.ok) {
    throw new Error(`media token failed: ${res.status}`);
  }
  const { token } = await res.json();
  const streamPath = resourceType === "video"
    ? `/videos/stream/${resourceId}`
    : resourceType === "form"
      ? `/forms/${resourceId}/pdf`
      : `/form-responses/${resourceId}/pdf`;
  return `${API}${streamPath}?auth=${encodeURIComponent(token)}`;
}

/** Hook: resolves to the streaming URL once the token is minted. */
export function useMediaSrc(resourceType, resourceId) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    if (!resourceId) return () => { cancelled = true; };
    mediaSrc(resourceType, resourceId)
      .then((u) => { if (!cancelled) setSrc(u); })
      .catch(() => { if (!cancelled) setSrc(null); });
    return () => { cancelled = true; };
  }, [resourceType, resourceId]);
  return src;
}

/**
 * Drop-in `<video>` replacement — fetches a scoped media token before
 * setting the src. Renders a `Loading…` placeholder while the token is
 * being minted so no full-scope JWT ever leaks into `src`.
 */
export function MediaVideo({ id, className = "", ...rest }) {
  const src = useMediaSrc("video", id);
  if (!src) {
    return <div className={`${className} bg-black/40 flex items-center justify-center text-xs text-zinc-400`}>Loading…</div>;
  }
  return <video src={src} className={className} {...rest} />;
}

/** Drop-in `<iframe>` replacement for form/response PDFs. */
export function MediaIframe({ resourceType, id, className = "", title = "PDF", ...rest }) {
  const src = useMediaSrc(resourceType, id);
  if (!src) {
    return <div className={`${className} bg-black/40 flex items-center justify-center text-xs text-zinc-400`}>Loading…</div>;
  }
  return <iframe src={src} className={className} title={title} {...rest} />;
}
